import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronRight,
  ShoppingCart,
  Star,
  Package,
  Truck,
  Shield,
  Phone,
  Minus,
  Plus,
  ShoppingBag,
  X,
  Trash2,
} from "lucide-react";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useCart } from "../../contexts/CartContext";
import "./product-detail.css";

/* ------------------------------------------------------------------ */
/* Cart drawer — same behaviour as ROnlineStorePage's StoreCartDrawer  */
/* (components/cart/CartSidebar.js), restyled to the deep-mine design.  */
/* Auto-opens on addToCart (CartContext sets isOpen:true on add) so the */
/* "Add to Cart" action gives visible feedback, matching the store.     */
/* ------------------------------------------------------------------ */
function StoreCartDrawer() {
  const {
    items,
    totalItems,
    subtotal,
    isCartOpen,
    setCartOpen,
    removeFromCart,
    updateQuantity,
    clearCart,
    formatPrice,
    getShippingFee,
    getShippingNote,
    getTotal,
  } = useCart();

  const shippingFee = getShippingFee();
  const total = getTotal();

  // Escape closes; lock body scroll while open (matches the old Sheet)
  useEffect(() => {
    if (!isCartOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setCartOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isCartOpen]);

  const handleQuantityChange = (productId, newQuantity) => {
    updateQuantity(productId, newQuantity);
  };

  return (
    <div className={"st-cartroot" + (isCartOpen ? " is-open" : "")} aria-hidden={!isCartOpen}>
      <div className="st-cartoverlay" onClick={() => setCartOpen(false)} />
      <aside className="st-cartpanel" role="dialog" aria-label="Shopping cart">
        <div className="st-cart__head">
          <div className="st-cart__title">
            <ShoppingBag size={17} />
            Shopping Cart ({totalItems} items)
          </div>
          <button
            type="button"
            className="st-cart__close"
            onClick={() => setCartOpen(false)}
            aria-label="Close cart"
          >
            <X size={16} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="st-cart__empty">
            <ShoppingCart size={46} className="st-cart__emptyicon" />
            <div className="st-cart__emptytitle">Your cart is empty</div>
            <p className="st-cart__emptytext">Add some beautiful opals to get started</p>
            <Link
              to="/opal-store"
              className="u-btn u-btn--primary st-cart__fullbtn"
              onClick={() => setCartOpen(false)}
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <>
            <div className="st-cart__items">
              {items.map((item) => {
                const price = parseFloat(item.product.price || item.product.regular_price || 0);
                const itemTotal = price * item.quantity;
                const productId = item.product._id || item.product.id;

                return (
                  <div key={`${productId}-${item.metal_type || ""}`} className="st-cart__item">
                    <div className="st-cart__thumb">
                      {item.product.images && item.product.images.length > 0 ? (
                        <img
                          src={item.product.images[0].src || item.product.images[0]}
                          alt={item.product.name}
                          onError={(e) => {
                            e.target.src = "https://placehold.co/64/64";
                          }}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <ShoppingBag size={20} />
                      )}
                    </div>
                    <div className="st-cart__info">
                      <div className="st-cart__name">{item.product.name}</div>
                      <div className="st-cart__each">{formatPrice(price)} each</div>
                      {item.metal_type && (
                        <div className="st-cart__metal">{item.metal_type}</div>
                      )}
                      <div className="st-cart__qty">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(productId, item.quantity - 1)}
                          aria-label="Decrease quantity"
                        >
                          <Minus size={12} />
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(productId, item.quantity + 1)}
                          aria-label="Increase quantity"
                        >
                          <Plus size={12} />
                        </button>
                        <button
                          type="button"
                          className="st-cart__trash"
                          onClick={() => removeFromCart(productId)}
                          aria-label="Remove item"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <div className="st-cart__linetotal">{formatPrice(itemTotal)}</div>
                  </div>
                );
              })}
            </div>

            <div className="st-cart__summary">
              <div className="st-cart__row">
                <span>Subtotal ({totalItems} items):</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="st-cart__row">
                <span>Shipping:</span>
                <span>{shippingFee === 0 ? "FREE" : formatPrice(shippingFee)}</span>
              </div>
              <div className="st-cart__row st-cart__row--total">
                <span>Total:</span>
                <span>{formatPrice(total)}</span>
              </div>
              <div className="st-cart__note">
                <Truck size={14} />
                <span>{getShippingNote()}</span>
              </div>
              <Link
                to="/checkout"
                className="u-btn u-btn--primary st-cart__fullbtn"
                onClick={() => setCartOpen(false)}
              >
                Proceed to Checkout
              </Link>
              <Link
                to="/opal-store"
                className="u-btn u-btn--ghost st-cart__fullbtn"
                onClick={() => setCartOpen(false)}
              >
                Continue Shopping
              </Link>
              {totalItems > 0 && (
                <button
                  type="button"
                  className="st-cart__clear"
                  onClick={() => {
                    clearCart();
                    setCartOpen(false);
                  }}
                >
                  Clear Cart
                </button>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */
const RProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedMetalType, setSelectedMetalType] = useState(null);

  const { addToCart, formatPrice } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`
      );

      if (!response.ok) {
        throw new Error("Product not found");
      }

      const productData = await response.json();
      setProduct(productData);
    } catch (err) {
      console.error("Error fetching product:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      // Check if metal type is required but not selected
      if (
        product.has_metal_options &&
        product.metal_options &&
        product.metal_options.length > 0 &&
        !selectedMetalType
      ) {
        alert("Please select a metal type");
        return;
      }
      addToCart(product, quantity, selectedMetalType);
    }
  };

  const handleCallForPrice = () => {
    window.location.href = "tel:+61886724588"; // (08) 8672-4588
  };

  /* -------- Loading state -------- */
  if (loading) {
    return (
      <MarketingShell pageClass="pg-product-detail">
        <SEOHead
          title="Loading Product"
          description="Loading product details from the Umoona Opal Mine online store."
          noIndex
        />
        <section className="pd-status">
          <div className="pd-spinner" />
          <p className="pd-status__text">Loading product...</p>
        </section>
      </MarketingShell>
    );
  }

  /* -------- Error / not-found state -------- */
  if (error || !product) {
    return (
      <MarketingShell pageClass="pg-product-detail">
        <SEOHead
          title="Product Not Found"
          description="The product you are looking for does not exist or is no longer available."
          noIndex
        />
        <section className="pd-status">
          <div className="pd-notfound">
            <div className="pd-notfound__icon">
              <ShoppingBag size={38} />
            </div>
            <h1 className="pd-notfound__title">Product Not Found</h1>
            <p className="pd-notfound__text">
              {error || "The product you are looking for does not exist."}
            </p>
            <Link to="/opal-store" className="u-btn u-btn--primary">
              <ArrowLeft size={16} style={{ marginRight: 8 }} />
              Back to Store
            </Link>
          </div>
        </section>
      </MarketingShell>
    );
  }

  /* -------- Loaded — pricing / image derivations (ported verbatim) -------- */
  const price = parseFloat(product.price || product.regular_price || 0);
  const regularPrice = parseFloat(product.regular_price || 0);
  const salePrice = product.sale_price ? parseFloat(product.sale_price) : null;
  const isOnSale = salePrice && salePrice < regularPrice;
  const displayPrice = isOnSale ? salePrice : price;

  const images = product.images || [];
  const primaryImage =
    images.length > 0 ? images[selectedImageIndex].src : "https://placehold.co/400/400";

  // Strip HTML from the description for the meta tag / SEO
  const plainDescription = product.description
    ? product.description.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    : `Authentic Australian opal from Umoona Opal Mine in Coober Pedy — ${product.name}.`;

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: plainDescription,
    sku: product.sku || undefined,
    image: images.length > 0 ? images.map((img) => img.src) : undefined,
    brand: { "@type": "Brand", name: "Umoona Opal Mine" },
    offers: {
      "@type": "Offer",
      priceCurrency: "AUD",
      price: displayPrice > 0 ? displayPrice.toFixed(2) : undefined,
      availability: "https://schema.org/InStock",
      url: `https://umoonaopalmine.com.au/opal-store/product/${productId}`,
    },
  };

  return (
    <MarketingShell pageClass="pg-product-detail">
      <SEOHead
        title={product.name}
        description={plainDescription}
        canonical={`/opal-store/product/${productId}`}
        ogImage={images.length > 0 ? images[0].src : undefined}
        ogType="product"
        schemaData={schemaData}
      />

      {/* ============ PRODUCT ============ */}
      <section className="u-sectpad u-sect-deep pd-top">
        {/* Breadcrumb */}
        <nav className="pd-crumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <ChevronRight size={13} className="pd-crumbs__sep" />
          <Link to="/opal-store">Opal Store</Link>
          {product.categories && product.categories.length > 0 && (
            <>
              <ChevronRight size={13} className="pd-crumbs__sep" />
              <span className="pd-crumbs__cat">{product.categories[0].name}</span>
            </>
          )}
          <ChevronRight size={13} className="pd-crumbs__sep" />
          <span className="pd-crumbs__current">{product.name}</span>
        </nav>

        {/* Back to store (react-router navigation — same destination as legacy) */}
        <button type="button" className="pd-back" onClick={() => navigate("/opal-store")}>
          <ArrowLeft size={15} />
          Back to Store
        </button>

        <div className="pd-layout">
          {/* ---- Gallery ---- */}
          <div className="pd-gallery">
            <div className="pd-main">
              <img
                src={primaryImage}
                alt={product.name}
                onError={(e) => {
                  e.target.src = "https://placehold.co/400/400";
                }}
              />
            </div>

            {images.length > 1 && (
              <div className="pd-thumbs">
                {images.slice(0, 4).map((image, index) => (
                  <button
                    type="button"
                    key={index}
                    className={"pd-thumb" + (selectedImageIndex === index ? " is-active" : "")}
                    onClick={() => setSelectedImageIndex(index)}
                    aria-label={`View image ${index + 1}`}
                  >
                    <img src={image.src} alt={`${product.name} ${index + 1}`} loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ---- Info ---- */}
          <div className="pd-info">
            {/* Header */}
            <div className="pd-head">
              <div className="pd-head__main">
                <h1 className="pd-title">{product.name}</h1>
                {product.sku && <div className="pd-sku">SKU: {product.sku}</div>}
              </div>
              {product.featured && (
                <span className="pd-featured">
                  <Star size={12} />
                  Featured
                </span>
              )}
            </div>

            {/* Categories */}
            {product.categories && product.categories.length > 0 && (
              <div className="pd-cats">
                {product.categories.map((category) => (
                  <span key={category.id} className="pd-cat">
                    {category.name}
                  </span>
                ))}
              </div>
            )}

            {/* Price */}
            <div className="pd-price">
              {isOnSale ? (
                <>
                  <span className="pd-price__now">{formatPrice(salePrice)}</span>
                  <span className="pd-price__was">{formatPrice(regularPrice)}</span>
                  <span className="pd-price__sale">SALE</span>
                </>
              ) : displayPrice > 0 ? (
                <span className="pd-price__now">{formatPrice(displayPrice)}</span>
              ) : (
                <div className="pd-price__call">
                  <span className="pd-price__callhead">Call for Price</span>
                  <span className="pd-price__callsub">
                    Contact us for pricing on this unique piece
                  </span>
                </div>
              )}
            </div>

            {/* Stock */}
            <div className="pd-stock">
              <Package size={17} />
              <span className="pd-stock__label">In Stock</span>
              {product.stock_quantity && (
                <span className="pd-stock__count">({product.stock_quantity} available)</span>
              )}
            </div>

            {/* Description */}
            {product.description && (
              <div className="pd-desc">
                <h3 className="pd-desc__title">Description</h3>
                <div
                  className="pd-desc__body"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              </div>
            )}

            {/* Metal Type Selection */}
            {product.has_metal_options &&
              product.metal_options &&
              product.metal_options.length > 0 && (
                <div className="pd-metals">
                  <h4 className="pd-metals__title">Select Metal Type *</h4>
                  <div className="pd-metals__opts">
                    {product.metal_options.includes("sterling_silver") && (
                      <button
                        type="button"
                        onClick={() => setSelectedMetalType("sterling_silver")}
                        className={
                          "pd-metal" +
                          (selectedMetalType === "sterling_silver" ? " is-active" : "")
                        }
                      >
                        <span className="pd-metal__name">Sterling Silver</span>
                      </button>
                    )}
                    {product.metal_options.includes("gold_plated") && (
                      <button
                        type="button"
                        onClick={() => setSelectedMetalType("gold_plated")}
                        className={
                          "pd-metal" +
                          (selectedMetalType === "gold_plated" ? " is-active" : "")
                        }
                      >
                        <span className="pd-metal__name">Sterling Silver Gold Plated</span>
                        <span className="pd-metal__sub">
                          Gold plated over 925 Sterling Silver
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              )}

            {/* Add to Cart / Call for Price */}
            <div className="pd-buy">
              {displayPrice > 0 ? (
                <div className="pd-buy__row">
                  <div className="pd-qty">
                    <button
                      type="button"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      aria-label="Decrease quantity"
                    >
                      <Minus size={15} />
                    </button>
                    <span className="pd-qty__val">{quantity}</span>
                    <button
                      type="button"
                      onClick={() => setQuantity(quantity + 1)}
                      aria-label="Increase quantity"
                    >
                      <Plus size={15} />
                    </button>
                  </div>
                  <button type="button" className="pd-addbtn" onClick={handleAddToCart}>
                    <ShoppingCart size={18} />
                    Add to Cart — {formatPrice(displayPrice * quantity)}
                  </button>
                </div>
              ) : (
                <button type="button" className="pd-callbtn" onClick={handleCallForPrice}>
                  <Phone size={18} />
                  Call for Price — (08) 8672-4588
                </button>
              )}
            </div>

            {/* Trust features */}
            <div className="pd-features">
              <div className="pd-feature">
                <Shield size={18} />
                <div>
                  <div className="pd-feature__title">Authentic</div>
                  <div className="pd-feature__sub">100% Australian</div>
                </div>
              </div>
              <div className="pd-feature">
                <Truck size={18} />
                <div>
                  <div className="pd-feature__title">Shipping</div>
                  <div className="pd-feature__sub">Australia Wide</div>
                </div>
              </div>
              <div className="pd-feature">
                <Package size={18} />
                <div>
                  <div className="pd-feature__title">Quality</div>
                  <div className="pd-feature__sub">Premium Grade</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="u-sectpad u-sect-cta">
        <div className="u-ctacenter">
          <h2 className="u-ctacenter__title">Keep exploring the collection</h2>
          <p className="u-ctacenter__sub">
            Browse more authentic Coober Pedy opal, or visit our store in person for expert
            guidance.
          </p>
          <div className="u-ctacenter__btns">
            <Link to="/online-store" className="u-btn u-btn--primary">
              Continue Shopping
            </Link>
            <Link to="/opal-store" className="u-btn u-btn--ghost">
              About the Store
            </Link>
          </div>
        </div>
      </section>

      {/* Cart drawer (auto-opens when an item is added) */}
      <StoreCartDrawer />
    </MarketingShell>
  );
};

export default RProductDetailPage;
